import React, { useMemo } from 'react'
import { SalesStage } from 'models/sales_stages'
import getStageColor from 'utils/getStageColor'
import { usePreferences } from 'context/preferences/PreferencesContext'

interface SalesStageProgressProps {
  currentStageId?: number
  salesStages?: SalesStage[]
  onStageChange?: (newStageId: number) => void
}

/**
 * Displays a visual progress indicator for sales stages.
 * Shows previous (80% opacity), current (full color), and next (gray) stages.
 * Terminal stages (Closed Won, Closed Lost, Churn) show celebration emojis.
 * When no stage is set, shows "No Stage" followed by the first available stage.
 */
export function SalesStageProgress({
  currentStageId,
  salesStages,
  onStageChange,
}: SalesStageProgressProps) {
  const { isSettingEnabled } = usePreferences()

  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  // Sort stages based on custom order preference
  const sortedStages = useMemo(() => {
    if (!salesStages) return []
    return [...salesStages].sort((a, b) =>
      customOrderEnabled
        ? a.order - b.order
        : (a.win_rate || 0) - (b.win_rate || 0)
    )
  }, [salesStages, customOrderEnabled])

  // Handle "No Stage" state
  if (!currentStageId || sortedStages.length === 0) {
    // Find the first stage for the next stage preview
    const firstRealStage = sortedStages[0]

    return (
      <div className="flex flex-1 items-center">
        <ChevronStage
          label="No Stage"
          color="#6E6E6E"
          textColor="#FFFFFF"
          isFirst={true}
          isCurrent={true}
          isLast={!firstRealStage}
          fullWidth={true}
        />
        {firstRealStage && (
          <div style={{ marginLeft: '-6px' }}>
            <ChevronStage
              label={firstRealStage.stage}
              color="#E2E2E2"
              textColor="#191D23"
              isFirst={false}
              isCurrent={false}
              isNext={true}
              onClick={() => onStageChange?.(firstRealStage.id)}
            />
          </div>
        )}
      </div>
    )
  }

  // Find current stage index
  const currentIndex = sortedStages.findIndex((s) => s.id === currentStageId)
  if (currentIndex === -1) return null

  const currentStage = sortedStages[currentIndex]
  const previousStage = currentIndex > 0 ? sortedStages[currentIndex - 1] : null
  const nextStage =
    currentIndex < sortedStages.length - 1
      ? sortedStages[currentIndex + 1]
      : null

  // Terminal stages have no next stage
  const isTerminalStage = ['Closed Won', 'Closed Lost', 'Churn'].includes(
    currentStage.stage
  )

  // Only Closed Won gets celebration emojis
  const showCelebration = currentStage.stage === 'Closed Won'

  // Get colors for current stage
  const [currentColor, currentTextColor] = getStageColor(
    currentStage.win_rate || 0
  )

  // Convert hex to rgba with 80% opacity for previous stage
  const addOpacity = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return (
    <div className="flex items-center">
      {/* Previous Stage - 80% opacity, clickable */}
      {previousStage && (
        <ChevronStage
          label={previousStage.stage}
          color={addOpacity(currentColor, 0.8)}
          textColor={currentTextColor}
          isFirst={true}
          isCurrent={false}
          isPrevious={true}
          onClick={() => onStageChange?.(previousStage.id)}
        />
      )}

      {/* Current Stage - Full color, bold, not clickable */}
      <div
        className={isTerminalStage ? 'flex-1' : ''}
        style={{ marginLeft: previousStage ? '-6px' : '0' }}
      >
        <ChevronStage
          label={
            showCelebration
              ? `${currentStage.stage}  🎉 🎉 🎉`
              : currentStage.stage
          }
          color={currentColor}
          textColor={currentTextColor}
          isFirst={!previousStage}
          isCurrent={true}
          isLast={isTerminalStage}
        />
      </div>

      {/* Next Stage - Gray, clickable */}
      {!isTerminalStage && nextStage && (
        <div className="flex-1" style={{ marginLeft: '-6px' }}>
          <ChevronStage
            label={nextStage.stage}
            color="#E2E2E2"
            textColor="#191D23"
            isFirst={false}
            isCurrent={false}
            isNext={true}
            onClick={() => onStageChange?.(nextStage.id)}
          />
        </div>
      )}
    </div>
  )
}

interface ChevronStageProps {
  label: string
  color: string
  textColor: string
  isFirst: boolean
  isCurrent: boolean
  isLast?: boolean
  fullWidth?: boolean
  isPrevious?: boolean
  isNext?: boolean
  onClick?: () => void
}

/**
 * Left inset notch component
 */
function LeftChevronNotch({
  color,
  isNext = false,
  isPrevious = false,
}: {
  color: string
  isNext?: boolean
  isPrevious?: boolean
}) {
  return (
    <svg
      width="8"
      height="24"
      viewBox="0 0 8 24"
      className={`flex-shrink-0 transition-colors ${
        isNext ? 'group-hover:fill-[#C4C4C4]' : ''
      } ${isPrevious ? 'group-hover:opacity-90' : ''}`}
    >
      <path d="M8 0 L8 24 L0 24 L8 12 L0 0 Z" fill={color} />
    </svg>
  )
}

/**
 * Right pointing chevron component
 */
function RightChevronPoint({
  color,
  isNext = false,
  isPrevious = false,
}: {
  color: string
  isNext?: boolean
  isPrevious?: boolean
}) {
  return (
    <svg
      width="8"
      height="24"
      viewBox="0 0 8 24"
      className={`flex-shrink-0 transition-colors ${
        isNext ? 'group-hover:fill-[#C4C4C4]' : ''
      } ${isPrevious ? 'group-hover:opacity-90' : ''}`}
    >
      <path d="M 0 0 L 8 12 L 0 24 Z" fill={color} />
    </svg>
  )
}

/**
 * Individual chevron-shaped stage indicator.
 * Composed of: [optional left notch] + main body + [optional right point]
 */
function ChevronStage({
  label,
  color,
  textColor,
  isFirst,
  isCurrent,
  isLast = false,
  fullWidth = false,
  isPrevious = false,
  isNext = false,
  onClick,
}: ChevronStageProps) {
  const isClickable = (isPrevious || isNext) && onClick

  return (
    <div
      className={`flex items-center ${fullWidth ? 'flex-1' : ''} ${
        isClickable ? 'cursor-pointer group' : ''
      }`}
      style={{ height: '24px' }}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {/* Left notch - not for first item */}
      {!isFirst && (
        <LeftChevronNotch
          color={color}
          isNext={isNext}
          isPrevious={isPrevious}
        />
      )}

      {/* Main body with text */}
      <div
        className={`flex items-center justify-center px-2 transition-colors ${
          fullWidth ? 'flex-1' : ''
        } ${isNext ? 'group-hover:bg-fg-muted' : ''} ${
          isPrevious ? 'group-hover:opacity-90' : ''
        }`}
        style={{ backgroundColor: color, height: '24px' }}
      >
        <span
          className={`whitespace-nowrap text-xxs leading-[1.8em] ${
            isCurrent ? 'font-semibold' : 'font-light'
          }`}
          style={{ color: textColor }}
        >
          {label}
        </span>
      </div>

      {/* Right point - not for last item */}
      {!isLast && (
        <RightChevronPoint
          color={color}
          isNext={isNext}
          isPrevious={isPrevious}
        />
      )}
    </div>
  )
}
